import {Alert, Button, Divider, Group, Modal, Stack} from "@mantine/core";
import SignatureCanvas from "react-signature-canvas";
import {useCallback, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {IconAlertCircle} from "@tabler/icons-react";

export type SignatureModalProps = {
  opened: boolean;
  onClose: () => void;
  onSubmit: (signature: { mimeType: string; dataUrl: string }) => void;
};

export const SignatureModal = (props: SignatureModalProps) => {
  const {opened, onClose, onSubmit} = props

  const ref = useRef<SignatureCanvas>(null);

  const {t} = useTranslation(["common"])

  const [error, setError] = useState<string | undefined>(undefined);

  const onClearButtonClick = useCallback(() => {
    ref.current?.clear();
  }, [])

  const onSubmitButtonClick = useCallback(() => {
    setError(undefined)

    if (!ref.current || ref.current.isEmpty()) {
      setError("Please provide a signature.")
      return;
    }

    const canvas = ref.current.getCanvas()

    onSubmit({
      mimeType: "image/png",
      dataUrl: trimCanvas(canvas).toDataURL("image/png"),
    })
  }, [onSubmit])

  return (
    <Modal opened={opened} onClose={onClose} title="Add your signature" centered size="lg">
      <Stack spacing="md">
        {error &&
          <Alert icon={<IconAlertCircle size="1rem"/>} title="Error" color="red" m="sm" radius="lg">
            Please provide a signature.
          </Alert>
        }
        <SignatureCanvas
          ref={ref}
          penColor="black"
          backgroundColor="rgba(255,255,255,1)"
          canvasProps={{
            width: 600,
            height: 220,
            style: {
              border: "2px solid var(--mantine-color-gray-4)",
              borderRadius: 12,
              background: "var(--mantine-color-body)",
              touchAction: "none",
            },
          }}
        />
        <Divider/>
        <Group position="apart">
          <Button variant="default" onClick={onClearButtonClick}>
            {t("common.action.clear", "Clear")}
          </Button>
          <Group>
            <Button variant="default" onClick={onClose}>
              {t("common.action.cancel", "Cancel")}
            </Button>
            <Button onClick={onSubmitButtonClick}>
              {t("common.action.signsubmit", "Sign & Submit")}
            </Button>
          </Group>
        </Group>
        <Divider/>
        <small>
          By clicking “Sign & Submit,” you consent to use of an electronic signature and certify this
          review. Your signature, timestamp, and device info may be stored for audit.
        </small>
      </Stack>
    </Modal>
  )
};

export function trimCanvas(source: HTMLCanvasElement): HTMLCanvasElement {
  const ctx = source.getContext("2d")!;
  const { width, height } = source;
  const pixels = ctx.getImageData(0, 0, width, height).data;

  let top = 0, left = 0, right = width - 1, bottom = height - 1;
  let found = false;

  // find top
  for (let y = 0; y < height && !found; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4 + 3; // alpha channel
      if (pixels[i] !== 0) { top = y; found = true; break; }
    }
  }

  // find bottom
  found = false;
  for (let y = height - 1; y >= top && !found; y--) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4 + 3;
      if (pixels[i] !== 0) { bottom = y; found = true; break; }
    }
  }

  // find left
  found = false;
  for (let x = 0; x < width && !found; x++) {
    for (let y = top; y <= bottom; y++) {
      const i = (y * width + x) * 4 + 3;
      if (pixels[i] !== 0) { left = x; found = true; break; }
    }
  }

  // find right
  found = false;
  for (let x = width - 1; x >= left && !found; x--) {
    for (let y = top; y <= bottom; y++) {
      const i = (y * width + x) * 4 + 3;
      if (pixels[i] !== 0) { right = x; found = true; break; }
    }
  }

  // nothing drawn → return original to keep your empty check logic simple
  if (top === 0 && left === 0 && right === width - 1 && bottom === height - 1) {
    return source;
  }

  const w = right - left + 1;
  const h = bottom - top + 1;

  const out = document.createElement("canvas");
  out.width = w;
  out.height = h;
  const octx = out.getContext("2d")!;
  // optional: fill white to avoid transparent background in PNGs
  octx.fillStyle = "#fff";
  octx.fillRect(0, 0, w, h);
  octx.drawImage(source, left, top, w, h, 0, 0, w, h);
  return out;
}
